import { useMemo } from "react";
import { falseProjects } from "utils/constants";
import { getDisplayInfo } from "utils/formatting";

export const tableColumnKeys: (keyof BuildingProjectTableRow)[] = [
    "carbonReductionMeasureType",
    "carbonReductionMeasureCategory",
    "yearApplied",
    "totalEnergyConsumptionSavings",
    "totalEnergyConsumptionSavingsElectricity",
    "totalEnergyConsumptionSavingsNaturalGas",
    "annualCarbonEmissionSavings",
    "lifetimeEmissionsSavings",
    "likeForLikeCost",
    "incrementalCost",
    "implementationStatus",
    "totalIncentives",
    "netTotalCost",
    "netIncrementalCost",
    "totalMeasureCost",
    "totalMeasureCostIntensity",
    "annualMeanLandlordUtilityCostSavings",
    "annualMeanTenantUtilityCostSavings",
    "netPresentValueIncremental",
    "internalRateOfReturnIncremental",
    "returnOnInvestmentIncremental",
    "simplePaybackIncremental",
    "marginalAbatementCostIncremental",
    "lifetimeCarbonCostsAvoided",
];

export function useFormattedCarbonReductionData(
    measures: CarbonReductionMeasure[],
    grossFloorArea: number,
    carbonReductionPlanUid: string
) {
    const formattedData = measures.map((measure) => ({
        ...measure,
        annualEnergyConsumptionSavings: measure.energyConsumptionSavingsTotal,
        annualEnergyConsumptionSavingsElectricity: measure.energyConsumptionSavingsElectricity,
        annualEnergyConsumptionSavingsNaturalGas: measure.energyConsumptionSavingsNaturalGas,
        totalEnergyConsumptionSavings: measure.energyConsumptionSavingsTotal,
        annualCarbonEmissionSavings: measure.annualMeanCarbonEmissionSavings,
        totalMeasureCost: measure.splitMeasureCost,
        likeForLikeCost: measure.splitLikeForLikeCost,
        incrementalCost: measure.splitIncrementalCost,
        totalIncentives: measure.totalIncentives,
        netTotalCost: measure.splitNetTotalCost,
        netIncrementalCost: measure.splitNetIncrementalCost,
        totalMeasureCostIntensity: measure.splitMeasureCost / grossFloorArea,
        totalEnergyConsumptionSavingsElectricity: measure.energyConsumptionSavingsElectricity,
        totalEnergyConsumptionSavingsNaturalGas: measure.energyConsumptionSavingsNaturalGas,
        netPresentValueTotal: measure.netPresentValueTotal,
        netPresentValueIncremental: measure.netPresentValueIncremental,
        internalRateOfReturnTotal: measure.internalRateOfReturnTotal,
        internalRateOfReturnIncremental: measure.internalRateOfReturnIncremental,
        implementationStatus: measure.implementationStatus,
        returnOnInvestmentIncremental: measure.returnOnInvestmentIncremental,
        returnOnInvestmentTotal: measure.returnOnInvestmentTotal,
        simplePaybackTotal: measure.simplePaybackYearsTotal >= 0 ? measure.simplePaybackYearsTotal : Infinity,
        simplePaybackIncremental:
            measure.simplePaybackYearsIncremental >= 0 ? measure.simplePaybackYearsIncremental : Infinity,
        marginalAbatementCostTotal: measure.marginalAbatementCostTotal,
        marginalAbatementCostIncremental: measure.marginalAbatementCostIncremental,
        lifetimeEmissionsSavings: measure.lifetimeTotalCarbonEmissionSavings,
        lifetimeCarbonCostsAvoided: measure.lifetimeCarbonCostsAvoided,
        carbonReductionPlanUid,
    }));

    const tableColumns =
        !formattedData || formattedData.length === 0
            ? []
            : tableColumnKeys.map((key) => {
                  if (typeof formattedData[0][key] === "string") {
                      const { formatFunction, humanReadable, tooltip } = getDisplayInfo(key);
                      return {
                          render: formatFunction,
                          key,
                          title: humanReadable,
                          tooltip,
                          columnClass: undefined,
                      };
                  }
                  const columnValues = formattedData.map((row) => row[key]) as number[];

                  const { formatFunction, humanReadable, unit, tooltip } = getDisplayInfo(key, columnValues);

                  return {
                      render: formatFunction,
                      key,
                      title: `${humanReadable} ${unit && `(${unit})`}`,
                      tooltip,
                      columnClass: undefined,
                  };
              });

    const dataSource = useMemo(() => {
        if (!formattedData) return [];

        const filteredData = formattedData.filter(
            (row) =>
                !falseProjects.includes(row.carbonReductionMeasureType) && row.carbonReductionMeasureType !== "unmapped"
        );

        return filteredData.map((row, index) => ({
            ...row,
            key: `${row.carbonReductionMeasureType ? row.carbonReductionMeasureType : "defaultKey"}-${index}`,
        }));
    }, [formattedData]);

    return {
        formattedData,
        tableColumns,
        dataSource,
    };
}
